import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ChipInput from '../../components/shared/ChipInput';
import { useSnackbar } from 'notistack';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventIcon from '@mui/icons-material/Event';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Page from '../Page';
import Loading from '../../components/shared/Loading';
import DirectUpload from '../../components/shared/DirectUpload';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_POST, UPDATE_POST } from '../../graphql/post';
import ServerError from '../error/ServerError';
import PostPreview from './PostPreview';
import config from '../../config';
import { useAuth } from '../../hooks/use-auth';

const tabs = [
  { value: 'content', label: 'Content' },
  { value: 'preview', label: 'Preview' },
];

const POST_TYPES = [
  {
    value: 'announcement',
    label: 'Announcement',
    tag: 'announcement',
    icon: CampaignIcon,
    color: '#4f46e5',
  },
  {
    value: 'fitness-tip',
    label: 'Fitness Tip',
    tag: 'fitness tip',
    icon: FitnessCenterIcon,
    color: '#22c55e',
  },
  {
    value: 'nutrition-tip',
    label: 'Nutrition Tip',
    tag: 'nutrition tip',
    icon: LocalFireDepartmentIcon,
    color: '#f97316',
  },
  {
    value: 'challenge',
    label: 'Challenge',
    tag: 'challenge',
    icon: EmojiEventsIcon,
    color: '#ec4899',
  },
  {
    value: 'motivation',
    label: 'Motivation',
    tag: 'motivation',
    icon: FavoriteBorderIcon,
    color: '#8b5cf6',
  },
  {
    value: 'event',
    label: 'Event',
    tag: 'event',
    icon: EventIcon,
    color: '#2563eb',
  },
];

const typeTags = POST_TYPES.map((type) => type.tag);

const statusLabels = {
  DRAFT: 'Draft',
  PUBLIC: 'Public',
  PREMIUM: 'Premium',
  PRO: 'Pro',
};

const stripHtml = (value) =>
  (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatSavedAt = (value) => {
  if (!value) {
    return 'just now';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'just now';
  }

  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getCoverImageUrl = (post) =>
  post?.coverImageUrl || post?.coverImages?.[0]?.url || '';

const PostEditor = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('content');
  const [post, setPost] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const editorRef = useRef();
  const id = params.id;
  const { error, loading, data } = useQuery(GET_POST, { variables: { id } });

  useEffect(() => {
    if (params.tab) {
      setCurrentTab(params.tab === 'details' ? 'content' : params.tab);
    }
  }, [params]);

  const [updatePost] = useMutation(UPDATE_POST, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    },
    onCompleted: (data) => {
      if (data?.updatePost) {
        setPost(data.updatePost);
      }
      enqueueSnackbar('Post saved', {
        variant: 'success',
        autoHideDuration: 2000,
      });
    },
  });

  useEffect(() => {
    setPost(data?.post);
  }, [data]);

  useEffect(() => {
    if (currentTab !== 'content') {
      return undefined;
    }

    const timeout = setTimeout(() => {
      const quill = editorRef.current?.getEditor?.();
      const editorContainer = quill?.container?.parentElement;
      if (!editorContainer) {
        return;
      }

      const toolbars = editorContainer.querySelectorAll(':scope > .ql-toolbar');
      toolbars.forEach((toolbar, index) => {
        if (index < toolbars.length - 1) {
          toolbar.remove();
        }
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [currentTab, post?._id]);

  const activePostType = useMemo(() => {
    const tags = (post?.tags || []).map((tag) => tag.toLowerCase());
    return POST_TYPES.find((type) => tags.includes(type.tag)) || POST_TYPES[0];
  }, [post?.tags]);

  const coverImageUrl = getCoverImageUrl(post);
  const previewText = stripHtml(post?.content);
  const commentCount = post?.replies?.length || 0;
  const currentUserName = auth.user?.displayName || auth.user?.email;

  const handleSave = (overrides = {}) => {
    if (!post) {
      return;
    }

    enqueueSnackbar('Saving...', { autoHideDuration: 1200 });
    updatePost({
      variables: {
        id: post._id,
        input: {
          title: post.title,
          content: post.content,
          contentFormat: 'HTML',
          tags: post.tags || [],
          status: overrides.status || post.status,
          coverImages: post.coverImages || [],
          videos: post.videos,
        },
      },
    });
  };

  const handleEditorChange = (value) => {
    setPost({ ...post, content: value });
  };

  const handleChange = (event) => {
    setPost({ ...post, [event.target.name]: event.target.value });
  };

  const handlePostTypeChange = (type) => {
    const existingTags = post.tags || [];
    const filteredTags = existingTags.filter(
      (tag) => !typeTags.includes(tag.toLowerCase())
    );
    setPost({ ...post, tags: [...filteredTags, type.tag] });
  };

  const handleUploadError = () => {
    enqueueSnackbar(`Media upload failed`, {
      variant: 'error',
      autoHideDuration: 2000,
    });
  };

  const handleUploadSuccess = (uploadedResponse) => {
    let newPost = { ...post, coverImages: [uploadedResponse.image] };
    if (uploadedResponse.videos) {
      newPost.videos = uploadedResponse.videos;
    }

    setPost(newPost);
  };

  const handleUploadRemove = () => {
    setPost({ ...post, coverImages: [], videos: null });
  };

  const handleAddTag = (tag) => {
    setPost({ ...post, tags: [...(post.tags || []), tag] });
  };

  const handleDeleteTag = (chip) => {
    const newTags = (post.tags || []).filter((item) => item !== chip);
    setPost({ ...post, tags: newTags });
  };

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
    navigate(`/trainer/posts/${post._id}/${value}`);
  };

  if (error) {
    return <ServerError message={error.message} />;
  }

  if (loading || !post) {
    return <Loading />;
  }

  return (
    <Page title={`Post Editor : ${config.appDisplayName}`}>
      <Container maxWidth={false} sx={{ py: 3 }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/trainer/posts')}
              sx={{
                color: '#334155',
                px: 0,
                mb: 1,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              Back to Posts
            </Button>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a' }}>
                {post.title || 'Untitled Post'}
              </Typography>
              <Chip
                label="Saved"
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 700,
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#334155', mt: 0.75 }}>
              Last saved: {formatSavedAt(post.updatedAt || post.createdAt)}
            </Typography>
          </Box>

          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() => handleSave({ status: 'DRAFT' })}
              sx={{ minHeight: 48, px: 3, textTransform: 'none' }}
            >
              Save as Draft
            </Button>
            <ButtonGroup
              variant="contained"
              sx={{
                boxShadow: 'none',
                '& .MuiButton-root': {
                  minHeight: 48,
                  bgcolor: '#4f46e5',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#4338ca' },
                },
              }}
            >
              <Button onClick={() => handleSave({ status: 'PUBLIC' })}>
                Publish
              </Button>
              <Button
                aria-label="publish options"
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <KeyboardArrowDownIcon />
              </Button>
            </ButtonGroup>
            <IconButton
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{
                border: '1px solid #cbd5e1',
                borderRadius: 1,
                width: 48,
                height: 48,
                bgcolor: '#fff',
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setCurrentTab('preview');
                  navigate(`/trainer/posts/${post._id}/preview`);
                  setMenuAnchor(null);
                }}
              >
                Preview
              </MenuItem>
              {post.webUrl && (
                <MenuItem
                  onClick={() => {
                    window.open(post.webUrl, '_blank', 'noopener,noreferrer');
                    setMenuAnchor(null);
                  }}
                >
                  Open published post
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card
              id="main"
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
              }}
            >
              <Tabs
                onChange={handleTabsChange}
                value={currentTab}
                textColor="secondary"
                indicatorColor="secondary"
                sx={{
                  px: 2,
                  borderBottom: '1px solid #e2e8f0',
                  '& .MuiTab-root': {
                    minHeight: 58,
                    fontWeight: 700,
                    textTransform: 'none',
                  },
                }}
              >
                {tabs.map((tab) => (
                  <Tab key={tab.value} label={tab.label} value={tab.value} />
                ))}
              </Tabs>

              {currentTab === 'content' ? (
                <CardContent sx={{ p: 3 }}>
                  <Box mb={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Post Type
                    </Typography>
                    <Grid container spacing={1.5}>
                      {POST_TYPES.map((type) => {
                        const Icon = type.icon;
                        const isActive = activePostType.value === type.value;
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={2} key={type.value}>
                            <Button
                              fullWidth
                              variant={isActive ? 'contained' : 'outlined'}
                              startIcon={<Icon />}
                              onClick={() => handlePostTypeChange(type)}
                              sx={{
                                minHeight: 52,
                                borderColor: isActive ? '#6d5dfc' : '#cbd5e1',
                                bgcolor: isActive ? '#eef2ff' : '#fff',
                                color: isActive ? '#4f46e5' : '#1e293b',
                                boxShadow: 'none',
                                textTransform: 'none',
                                '&:hover': {
                                  bgcolor: isActive ? '#e0e7ff' : '#f8fafc',
                                  borderColor: '#6d5dfc',
                                  boxShadow: 'none',
                                },
                                '& .MuiButton-startIcon': { color: type.color },
                              }}
                            >
                              {type.label}
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>

                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Title
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {(post.title || '').length}/120
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      name="title"
                      onChange={handleChange}
                      value={post.title || ''}
                      variant="outlined"
                      inputProps={{ maxLength: 120 }}
                    />
                  </Box>

                  <Box mb={3}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Cover Image / Video{' '}
                      <Box component="span" sx={{ color: '#475569', fontWeight: 500 }}>
                        (Recommended)
                      </Box>
                    </Typography>
                    <DirectUpload
                      fileType="video"
                      images={post.coverImages || []}
                      onError={handleUploadError}
                      onSuccess={handleUploadSuccess}
                      onRemove={handleUploadRemove}
                      streaming={true}
                      maxFiles={1}
                      style={{
                        minHeight: 144,
                        border: '1px dashed #cbd5e1',
                        borderRadius: 8,
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 16,
                      }}
                    />
                    <Box
                      mt={-9}
                      mb={6}
                      display={{ xs: 'none', md: 'flex' }}
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      sx={{ pointerEvents: 'none', color: '#4f46e5' }}
                    >
                      <ImageOutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {coverImageUrl ? 'Replace image' : 'Add image or video'}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      display="block"
                      align="center"
                      sx={{ color: '#64748b', mt: coverImageUrl ? -5 : -4, mb: 3 }}
                    >
                      JPG, PNG, GIF or MP4 (Max. 50MB)
                    </Typography>
                  </Box>

                  <Box mb={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Content
                    </Typography>
                    <Box
                      sx={{
                        '& .ql-toolbar': {
                          borderColor: '#cbd5e1',
                          borderTopLeftRadius: 6,
                          borderTopRightRadius: 6,
                        },
                        '& .ql-container': {
                          minHeight: 150,
                          borderColor: '#cbd5e1',
                          borderBottomLeftRadius: 6,
                          borderBottomRightRadius: 6,
                          fontSize: 15,
                        },
                      }}
                    >
                      <ReactQuill
                        ref={editorRef}
                        theme="snow"
                        value={post.content || ''}
                        bounds="#main"
                        onChange={handleEditorChange}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      align="right"
                      sx={{ color: '#475569', mt: 1 }}
                    >
                      Characters : {stripHtml(post.content || '').length}
                    </Typography>
                  </Box>

                  <Box mb={3}>
                    <FormControl fullWidth>
                      <ChipInput
                        label="Add Tags"
                        value={post.tags ?? []}
                        onAdd={handleAddTag}
                        onDelete={handleDeleteTag}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                      <FormHelperText>
                        Add helpful tags for users to find your post
                      </FormHelperText>
                    </FormControl>
                  </Box>

                  <Box maxWidth={360}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel htmlFor="status">Status</InputLabel>
                      <Select
                        native
                        value={post.status}
                        onChange={handleChange}
                        label="Status"
                        inputProps={{
                          name: 'status',
                          id: 'status',
                        }}
                      >
                        <option value="DRAFT">Draft</option>
                        <option value="PUBLIC">Public</option>
                        <option value="PREMIUM">Premium</option>
                        <option value="PRO">Pro</option>
                      </Select>
                      <FormHelperText>
                        Public posts are visible to all members in your community.
                        Drafts are only visible to you. Premium content is visible
                        to premium members only.
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </CardContent>
              ) : (
                <CardContent sx={{ p: 3 }}>
                  <PostPreview post={post} />
                </CardContent>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
                mb: 2,
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                  Post Preview
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                  }}
                >
                  <Box display="flex" alignItems="center" p={2} pb={1}>
                    <Avatar
                      src={post.user?.avatarUrl}
                      sx={{ width: 44, height: 44, mr: 1.5 }}
                    >
                      {post.user?.name?.[0]}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {post.user?.name || currentUserName || 'Unknown author'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {formatSavedAt(post.createdAt)}
                      </Typography>
                    </Box>
                    <MoreVertIcon fontSize="small" sx={{ color: '#334155' }} />
                  </Box>
                  {coverImageUrl && (
                    <Box
                      component="img"
                      src={coverImageUrl}
                      alt=""
                      sx={{
                        width: '100%',
                        aspectRatio: '16 / 7',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                  <Box p={2}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                      {post.title || 'Untitled Post'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#334155', mb: 1, lineHeight: 1.6 }}
                    >
                      {previewText || 'Add post content to preview it here.'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={3} mt={2}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <FavoriteBorderIcon sx={{ color: '#e11d48' }} />
                        <Typography variant="body2">0</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ChatBubbleOutlineIcon />
                        <Typography variant="body2">{commentCount}</Typography>
                      </Box>
                      <Box flex={1} />
                      <BookmarkBorderIcon />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box mb={2.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="status"
                      value={post.status}
                      onChange={handleChange}
                    >
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="PUBLIC">Public</MenuItem>
                      <MenuItem value="PREMIUM">Premium</MenuItem>
                      <MenuItem value="PRO">Pro</MenuItem>
                    </Select>
                  </FormControl>
                  <FormHelperText>
                    Status controls who can see this post.
                  </FormHelperText>
                </Box>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Visibility
                </Typography>
                <Box display="flex" alignItems="center" gap={1.25}>
                  <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#475569' }} />
                  <Chip
                    label={statusLabels[post.status] || post.status}
                    size="small"
                    sx={{
                      bgcolor: post.status === 'DRAFT' ? '#f1f5f9' : '#dcfce7',
                      color: post.status === 'DRAFT' ? '#475569' : '#15803d',
                      fontWeight: 700,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {post.status === 'DRAFT'
                      ? 'Only visible to you'
                      : 'Visible to members'}
                  </Typography>
                  <Box flex={1} />
                  <CheckCircleIcon fontSize="small" sx={{ color: '#22c55e' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default PostEditor;
